import { Link } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/Seo";
import PageLayout from "@/components/layout/PageLayout";
import { site } from "@/mocks/site";
import { testimonials } from "@/mocks/content";

const values = [
  {
    icon: "ri-quill-pen-line",
    title: "编辑守则",
    desc: "信息标注来源、观点归属清楚，只做整理不做审判，拒绝针对个人的情绪化传播。",
  },
  {
    icon: "ri-layout-masonry-line",
    title: "结构第一",
    desc: "时间是骨架、人物是关节、观点是血肉。任何路线都先搭结构，再填充判断。",
  },
  {
    icon: "ri-history-line",
    title: "可勘误",
    desc: "整理依赖公开信息，难免有偏差。发现即勘误并保留修订记录，对读者负责。",
  },
  {
    icon: "ri-git-merge-line",
    title: "保持克制",
    desc: "不把「未证实」当结论，不把「不回应」当默认。克制，是让讨论不失控的底线。",
  },
];

const aboutSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL + "/" },
        { "@type": "ListItem", position: 2, name: "关于我们", item: SITE_URL + "/about" },
      ],
    },
    {
      "@type": "AboutPage",
      name: "关于吃瓜路线",
      url: SITE_URL + "/about",
      description: "吃瓜路线是面向全网公众的事件整理平台，用时间线、关键人物、观点光谱与信息溯源把复杂公共讨论串成清晰地图。",
      about: { "@type": "Organization", name: "吃瓜路线" },
    },
  ],
};

export default function About() {
  return (
    <PageLayout>
      <Seo
        title="关于我们 - 吃瓜路线"
        description="了解吃瓜路线：一个用时间线、关键人物、观点光谱与信息溯源，把复杂公共讨论整理成清晰事件地图的信息平台及其编辑守则与联系方式。"
        keywords="吃瓜路线关于,编辑守则,内容原则,联系吃瓜路线,事件整理平台"
        path="/about"
        schema={aboutSchema}
      />

      <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
        <ol className="mx-auto flex w-full max-w-7xl items-center gap-2 px-4 py-3 text-xs text-foreground-500 md:px-6">
          <li>
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
          </li>
          <li className="text-foreground-300">/</li>
          <li aria-current="page" className="font-medium text-foreground-700">
            关于我们
          </li>
        </ol>
      </nav>

      {/* 品牌介绍 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-700">
              ABOUT · 关于我们
            </p>
            <h1 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
              我们想做「信息时代的清醒旁观者」的引路人
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              {site.name}是一个面向全网公众的<strong>事件整理</strong>平台。我们不生产情绪与流量，只做一件事：把散落在社交平台、媒体与公告里的零散线索，用时间线、关键人物、观点光谱与信息溯源重新组织成一张清楚的事件地图——让任何人在围观复杂公共话题时，都不再云里雾里。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/events"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                浏览事件地图
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
              <Link
                to="/methodology"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              >
                阅读方法论
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: "ri-node-tree", t: "线索串联", d: "把零散信息按结构与时间重新排布" },
              { icon: "ri-road-map-line", t: "事件地图", d: "一张图看懂一件事的来龙去脉" },
              { icon: "ri-scales-3-line", t: "观点光谱", d: "各方立场并置，冲突点一目了然" },
              { icon: "ri-search-eye-line", t: "信息溯源", d: "已证实与待核实清晰分层" },
            ].map((c) => (
              <div
                key={c.t}
                className="rounded-lg border border-background-200 bg-background-50 p-5 text-center"
              >
                <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className={`${c.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-900">{c.t}</h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 理念与原则 */}
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            我们相信的几条理念
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-foreground-600">
            信息越多，越需要秩序。与其被算法与热搜推着走，不如掌握一套自己的整理方法。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div
                key={v.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-800">
                  <i className={`${v.icon} text-lg`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-3 text-sm font-bold text-foreground-900">{v.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 口碑 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">读者怎么说</h2>
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {testimonials.map((t) => (
            <figure key={t.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-0.5 text-accent-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-sm" aria-hidden="true"></i>
                ))}
              </div>
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                “{t.text}”
              </blockquote>
              <figcaption className="mt-4 border-t border-background-200 pt-3 text-sm">
                <span className="block font-semibold text-foreground-900">{t.name}</span>
                <span className="text-xs text-foreground-500">{t.tag}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* 联系方式 */}
      <section className="mx-auto w-full max-w-7xl px-4 pb-14 md:px-6 md:pb-20">
        <div className="rounded-lg bg-gradient-to-br from-primary-600 via-primary-500 to-accent-500 p-8 text-background-50 md:p-10">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div>
              <h2 className="font-heading text-2xl font-black md:text-3xl">联系我们</h2>
              <p className="mt-2 max-w-xl text-sm text-background-50/85">
                提交勘误、转载合作或任何建议，欢迎来信。我们会在核实后认真回复每一条反馈。
              </p>
              <a
                href={`mailto:${site.email}`}
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-700 hover:bg-accent-50"
              >
                <i className="ri-mail-line" aria-hidden="true"></i>
                {site.email}
              </a>
            </div>
            <div className="grid w-full max-w-xs grid-cols-2 gap-3 rounded-lg bg-background-50/10 p-4 text-sm backdrop-blur">
              <div>
                <p className="text-xs text-background-50/70">回复时效</p>
                <p className="mt-1 font-medium">1–3 个工作日</p>
              </div>
              <div>
                <p className="text-xs text-background-50/70">适合联系</p>
                <p className="mt-1 font-medium">勘误 / 转载 / 建议</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}